import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { Button } from '../components/ui.js'
import { cn } from '../lib/cn.js'

/**
 * Lighting up one thing on the screen: the tour's steps and the help's
 * "Show me". Decision 115.
 *
 * The target is found by selector and re-measured while the spotlight is up,
 * because the screen behind it is live — the tour opens each screen as it
 * describes it, and a list that loads pushes things down. A target that never
 * appears is not an error: the card is centred and still says its piece.
 */

/** How long to wait for a target after a step opens its screen. */
const FIND_FOR_MS = 3000
const REMEASURE_MS = 200
const GAP = 14
const MARGIN = 12
const RING = 6

interface Box {
  top: number
  left: number
  width: number
  height: number
}

function boxOf(element: Element): Box | null {
  const rect = element.getBoundingClientRect()
  if (rect.width === 0 && rect.height === 0) return null
  return { top: rect.top, left: rect.left, width: rect.width, height: rect.height }
}

function same(a: Box | null, b: Box | null) {
  if (!a || !b) return a === b
  return (
    Math.abs(a.top - b.top) < 0.5 &&
    Math.abs(a.left - b.left) < 0.5 &&
    Math.abs(a.width - b.width) < 0.5 &&
    Math.abs(a.height - b.height) < 0.5
  )
}

export type TargetState = { box: Box | null; status: 'looking' | 'found' | 'missing' }

/** Where the target is now, re-read on resize, scroll and a short interval. */
export function useTarget(selector: string | undefined): TargetState {
  const [state, setState] = useState<TargetState>({
    box: null,
    status: selector ? 'looking' : 'missing',
  })

  useEffect(() => {
    if (!selector) {
      setState({ box: null, status: 'missing' })
      return
    }
    setState({ box: null, status: 'looking' })
    const started = Date.now()
    let scrolled = false
    let frame = 0

    const measure = () => {
      let element: Element | null = null
      try {
        element = document.querySelector(selector)
      } catch {
        element = null
      }
      const box = element ? boxOf(element) : null
      if (element && box && !scrolled) {
        scrolled = true
        element.scrollIntoView?.({ block: 'nearest', inline: 'nearest' })
      }
      setState((was) => {
        if (box) return same(was.box, box) && was.status === 'found' ? was : { box, status: 'found' }
        if (Date.now() - started > FIND_FOR_MS) {
          return was.status === 'missing' && !was.box ? was : { box: null, status: 'missing' }
        }
        return was
      })
    }

    const soon = () => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(measure)
    }

    measure()
    const timer = window.setInterval(measure, REMEASURE_MS)
    window.addEventListener('resize', soon)
    window.addEventListener('scroll', soon, true)
    return () => {
      window.clearInterval(timer)
      cancelAnimationFrame(frame)
      window.removeEventListener('resize', soon)
      window.removeEventListener('scroll', soon, true)
    }
  }, [selector])

  return state
}

type Side = 'right' | 'left' | 'top' | 'bottom' | 'centre'

interface Placement {
  top: number
  left: number
  side: Side
  /** Where the little pointer sits along the card's edge, in px. */
  caret: number
}

/** Beside the target where there is room, preferring the side away from the screen's edge. */
function place(target: Box | null, card: { width: number; height: number }): Placement {
  const vw = window.innerWidth
  const vh = window.innerHeight
  const centre = {
    top: Math.max(MARGIN, (vh - card.height) / 2),
    left: Math.max(MARGIN, (vw - card.width) / 2),
    side: 'centre' as const,
    caret: 0,
  }
  if (!target) return centre

  const clampTop = (top: number) => Math.min(Math.max(MARGIN, top), vh - card.height - MARGIN)
  const clampLeft = (left: number) => Math.min(Math.max(MARGIN, left), vw - card.width - MARGIN)
  const midY = target.top + target.height / 2
  const midX = target.left + target.width / 2

  const room = {
    right: vw - (target.left + target.width) - GAP - RING,
    left: target.left - GAP - RING,
    bottom: vh - (target.top + target.height) - GAP - RING,
    top: target.top - GAP - RING,
  }

  if (room.right >= card.width + MARGIN) {
    const top = clampTop(midY - card.height / 2)
    return { side: 'right', top, left: target.left + target.width + GAP + RING, caret: midY - top }
  }
  if (room.left >= card.width + MARGIN) {
    const top = clampTop(midY - card.height / 2)
    return { side: 'left', top, left: target.left - GAP - RING - card.width, caret: midY - top }
  }
  if (room.top >= card.height + MARGIN && (room.top > room.bottom || room.bottom < card.height + MARGIN)) {
    const left = clampLeft(midX - card.width / 2)
    return { side: 'top', top: target.top - GAP - RING - card.height, left, caret: midX - left }
  }
  if (room.bottom >= card.height + MARGIN) {
    const left = clampLeft(midX - card.width / 2)
    return { side: 'bottom', top: target.top + target.height + GAP + RING, left, caret: midX - left }
  }
  return centre
}

/** The whole window, with the target (and its ring's gap) cut out as an 8px-cornered hole. */
function dimPath(box: Box | null): string {
  const outer = `M0 0H${window.innerWidth}V${window.innerHeight}H0Z`
  if (!box) return outer
  const x = box.left - RING
  const y = box.top - RING
  const w = box.width + RING * 2
  const h = box.height + RING * 2
  const r = Math.min(8, w / 2, h / 2)
  return (
    `${outer}M${x + r} ${y}H${x + w - r}A${r} ${r} 0 0 1 ${x + w} ${y + r}V${y + h - r}` +
    `A${r} ${r} 0 0 1 ${x + w - r} ${y + h}H${x + r}A${r} ${r} 0 0 1 ${x} ${y + h - r}` +
    `V${y + r}A${r} ${r} 0 0 1 ${x + r} ${y}Z`
  )
}

/**
 * The dimmed screen, the lit target and a card beside it. The whole layer
 * takes the pointer, so a stray click during a tour cannot book a visit.
 */
export function SpotlightFrame({
  box,
  field,
  labelledBy,
  cardClassName,
  children,
}: {
  box: Box | null
  field: boolean
  labelledBy: string
  cardClassName?: string
  children: ReactNode
}) {
  const card = useRef<HTMLDivElement>(null)
  const [size, setSize] = useState({ width: field ? 340 : 360, height: 220 })
  const [, setViewport] = useState(() => `${window.innerWidth}x${window.innerHeight}`)

  // The dim and the card's place are both read from the window's size.
  useEffect(() => {
    const onResize = () => setViewport(`${window.innerWidth}x${window.innerHeight}`)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  useLayoutEffect(() => {
    const element = card.current
    if (!element) return
    const read = () => {
      const rect = element.getBoundingClientRect()
      setSize((was) =>
        Math.abs(was.width - rect.width) < 1 && Math.abs(was.height - rect.height) < 1
          ? was
          : { width: rect.width, height: rect.height },
      )
    }
    read()
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(read)
    observer.observe(element)
    return () => observer.disconnect()
  })

  const at = place(box, size)
  const caretAt = Math.min(Math.max(at.caret, 18), (at.side === 'top' || at.side === 'bottom' ? size.width : size.height) - 18)

  return createPortal(
    <div className="fixed inset-0 z-[90]" data-guide="spotlight">
      {/* The dim is one path with the target cut out of it, rather than a
          9999px shadow round the ring: Chrome on a heavy screen and Safari on
          an iPad both leave a shadow that size unpainted for a moment. */}
      <svg aria-hidden="true" className="fixed inset-0 h-full w-full">
        <path fill="rgb(30 35 32 / 0.58)" fillRule="evenodd" d={dimPath(box)} />
      </svg>
      {box ? (
        <div
          aria-hidden="true"
          className="pointer-events-none fixed rounded-lg ring-2 ring-brand-500"
          style={{
            top: box.top - RING,
            left: box.left - RING,
            width: box.width + RING * 2,
            height: box.height + RING * 2,
          }}
        >
          <span className="absolute inset-0 animate-[guide-pulse_1.8s_ease-out_infinite] rounded-lg ring-2 ring-brand-400 motion-reduce:animate-none" />
        </div>
      ) : null}

      <div
        ref={card}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        className={cn(
          'fixed rounded-lg bg-white shadow-[0_24px_60px_rgb(15_23_42_/_0.3),0_6px_16px_rgb(15_23_42_/_0.14)] ring-1 ring-ink-200 transition-[top,left] duration-300 ease-out outline-none motion-reduce:transition-none',
          field ? 'w-[min(340px,calc(100vw-24px))]' : 'w-[360px]',
          cardClassName,
        )}
        style={{ top: at.top, left: at.left }}
      >
        {at.side !== 'centre' ? (
          <span
            aria-hidden="true"
            className={cn(
              'absolute size-3 rotate-45 bg-white ring-1 ring-ink-200',
              at.side === 'right' && '-left-1.5 [clip-path:polygon(0_0,0_100%,100%_100%)]',
              at.side === 'left' && '-right-1.5 [clip-path:polygon(0_0,100%_0,100%_100%)]',
              at.side === 'top' && '-bottom-1.5 [clip-path:polygon(100%_0,100%_100%,0_100%)]',
              at.side === 'bottom' && '-top-1.5 [clip-path:polygon(0_0,100%_0,0_100%)]',
            )}
            style={
              at.side === 'right' || at.side === 'left'
                ? { top: caretAt - 6 }
                : { left: caretAt - 6 }
            }
          />
        ) : null}
        {children}
      </div>
    </div>,
    document.body,
  )
}

/**
 * The button said "Show me where to add a task"; under "Here it is" the card
 * names the thing: "Add a task".
 */
function spotTitle(label: string): string {
  const rest = label.replace(/^show me (where to |how to )?/i, '')
  return rest.charAt(0).toUpperCase() + rest.slice(1)
}

/** "Show me": the screen, the thing lit up, and one button to carry on. */
export function Spotlight({
  selector,
  label,
  field,
  onDone,
}: {
  selector: string
  label: string
  field: boolean
  onDone: () => void
}) {
  const target = useTarget(selector)
  const done = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    // Nothing to point at after all: the screen itself was the answer.
    if (target.status === 'missing') onDone()
  }, [target.status, onDone])

  useEffect(() => {
    if (target.status === 'found') done.current?.focus()
  }, [target.status])

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' || event.key === 'Enter') {
        event.preventDefault()
        event.stopPropagation()
        onDone()
      }
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [onDone])

  if (target.status !== 'found') return null

  return (
    <SpotlightFrame box={target.box} field={field} labelledBy="guide-spotlight-title" cardClassName="w-[280px]">
      <div className="flex items-center gap-3 p-3.5">
        <div className="min-w-0 flex-1">
          <p className="text-[10.5px] font-bold tracking-wider text-brand-700 uppercase">Here it is</p>
          <p id="guide-spotlight-title" className="text-sm leading-snug font-semibold text-ink-900">
            {spotTitle(label)}
          </p>
        </div>
        <Button ref={done} size="sm" variant="primary" onClick={onDone}>
          Got it
        </Button>
      </div>
    </SpotlightFrame>
  )
}
